import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import {
  Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, IconButton,
  MenuItem, Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import { Confirm, useNotify } from "react-admin";
import { cloError, cloGet, cloPatch, cloPost, ENDPOINTS, formatDate, fromMinor, toMinor } from "./api";

const CONDITION_TYPES: Record<string, string> = {
  retailer_purchase: "Shopped at this retailer",
  group_purchase: "Shopped at a competitor group",
  group_activity: "Purchases / spend at a competitor group",
  channel_purchase: "Shopped at this retailer by channel",
  median_basket: "Usual basket at this retailer",
  spend: "Spend at this retailer",
};
const CONDITION_HELP: Record<string, string> = {
  retailer_purchase:
    "At this campaign's retailer, from bank payments, Sprive gift cards (SWS) and VDX purchases. Once a customer is in the campaign, their linked-card (Visa) purchases count too, whichever 'Paid' option is picked; Visa is not used when they are first placed.",
  group_purchase: "A bank payment, Sprive gift card (SWS) or VDX purchase at any shop in the chosen group (Competitor groups tab).",
  group_activity: "Totals across every shop in the group over the purchase & spend days set above. Fill one or both minimums; the customer must reach every minimum filled in.",
  channel_purchase: "Shopped at this retailer online or in store, from the bank payment text.",
  median_basket: "The customer's typical spend per visit. Needs the minimum purchases set above, otherwise this condition is not met.",
  spend: "Total bank spend at this retailer over the purchase & spend days set above.",
};
const RECENCY_OPS: Record<string, string> = { within: "in the last", not_within: "not in the last", never: "never" };
const AMOUNT_OPS: Record<string, string> = { gte: "at least", lt: "less than" };
const SOURCES: Record<string, string> = {
  any: "any way",
  bank: "by bank payment",
  gift_card: "with a Sprive gift card (SWS)",
  vdx: "through VDX",
};
const CHANNELS: Record<string, string> = { online: "online", in_store: "in store" };
const AMOUNT_TYPES = new Set(["median_basket", "spend"]);

const CONDITION_TEMPLATES: Record<string, any> = {
  retailer_purchase: { type: "retailer_purchase", source: "any", op: "within", days: "90" },
  group_purchase: { type: "group_purchase", groupId: "", op: "within", days: "365" },
  group_activity: { type: "group_activity", groupId: "", minTxns: "3", minSpend: "" },
  channel_purchase: { type: "channel_purchase", channel: "online", op: "within", days: "90" },
  median_basket: { type: "median_basket", op: "gte", pounds: "20" },
  spend: { type: "spend", op: "gte", pounds: "100" },
};

interface RuleForm {
  uid: string;
  name: string;
  targeted: boolean;
  isRemainder: boolean;
  criteria: any[];
}

const EXISTING = "Existing";

interface DefinitionForm {
  id?: number;
  version?: number;
  status?: string;
  dryRunAt?: string | null;
  atvWindowDays: string;
  spendWindowDays: string;
  atvMinTxns: string;
  defaultMedian: string;
  rules: RuleForm[];
}

let uidCounter = 0;
const nextUid = (): string => `rule-${Date.now()}-${uidCounter++}`;

const rule = (name: string, criteria: any[], extra: Partial<RuleForm> = {}): RuleForm => ({
  uid: nextUid(), name, targeted: true, isRemainder: false, criteria, ...extra,
});

const DEFAULT_RULES = (): RuleForm[] => [
  rule("Existing", [{ type: "retailer_purchase", source: "any", op: "within", days: "90" }]),
  rule("Lapsed", [
    { type: "retailer_purchase", source: "any", op: "not_within", days: "90" },
    { type: "retailer_purchase", source: "any", op: "within", days: "365" },
  ]),
  rule("Competitor", [
    { type: "retailer_purchase", source: "any", op: "never" },
    { type: "group_purchase", groupId: "", op: "within", days: "365" },
  ]),
  rule("New", [], { isRemainder: true }),
];

const rulesFromPricing = (names: string[]): RuleForm[] => {
  const others = names.filter((n) => n !== EXISTING);
  if (!others.length) return DEFAULT_RULES();
  const remainder = others.includes("New") ? "New" : others[others.length - 1];
  return [
    DEFAULT_RULES()[0],
    ...others.filter((n) => n !== remainder).map((n) => rule(n, [])),
    rule(remainder, [], { isRemainder: true }),
  ];
};

const toConditionForm = (c: any): any => {
  if (AMOUNT_TYPES.has(c.type)) return { type: c.type, op: c.op, pounds: fromMinor(c.minor) };
  if (c.type === "group_activity") {
    return { type: c.type, groupId: c.groupId, minTxns: c.minTxns ? String(c.minTxns) : "", minSpend: fromMinor(c.minSpendMinor ?? null) };
  }
  return { ...c, ...(c.days === undefined ? {} : { days: String(c.days) }) };
};

const fromDefinition = (d: any): DefinitionForm => ({
  id: d.id,
  version: d.version,
  status: d.status,
  dryRunAt: d.dryRunAt ?? null,
  atvWindowDays: String(d.atvWindowDays),
  spendWindowDays: String(d.spendWindowDays),
  atvMinTxns: String(d.atvMinTxns),
  defaultMedian: fromMinor(d.defaultMedianMinor),
  rules: d.rules.map((r: any) =>
    rule(r.name, (r.criteria || []).map(toConditionForm), { targeted: r.targeted, isRemainder: r.isRemainder })
  ),
});

const snapshot = (f: DefinitionForm): string =>
  JSON.stringify({ ...f, dryRunAt: undefined, rules: f.rules.map(({ uid, ...r }) => r) });

const toCondition = (c: any): any => {
  const { type, op } = c;
  if (AMOUNT_TYPES.has(type)) return { type, op, minor: toMinor(String(c.pounds)) };
  if (type === "group_activity") {
    return {
      type,
      groupId: Number(c.groupId),
      ...(c.minTxns.trim() ? { minTxns: Number(c.minTxns) } : {}),
      ...(c.minSpend.trim() ? { minSpendMinor: toMinor(c.minSpend) } : {}),
    };
  }
  const days = op === "never" ? {} : { days: Number(c.days) };
  if (type === "group_purchase") return { type, groupId: Number(c.groupId), op, ...days };
  if (type === "channel_purchase") return { type, channel: c.channel, op, ...days };
  return { type, source: c.source, op, ...days };
};

const toBody = (form: DefinitionForm): any => ({
  atvWindowDays: Number(form.atvWindowDays),
  spendWindowDays: Number(form.spendWindowDays),
  atvMinTxns: Number(form.atvMinTxns),
  defaultMedianMinor: toMinor(form.defaultMedian),
  rules: form.rules.map((r, i) => ({
    name: r.name.trim(),
    priority: i + 1,
    criteria: r.isRemainder ? [] : r.criteria.map(toCondition),
    isRemainder: r.isRemainder,
    targeted: r.name.trim() === EXISTING ? true : r.targeted,
  })),
});

const isWholeNumber = (value: string, min: number): boolean => /^\d+$/.test(value.trim()) && Number(value) >= min;
const isPounds = (value: string): boolean => /^\d+(\.\d{1,2})?$/.test(String(value).trim());

const formError = (form: DefinitionForm): string | null => {
  if (!isWholeNumber(form.atvWindowDays, 1)) return "Usual basket: days of history must be a whole number of 1 or more";
  if (!isWholeNumber(form.atvMinTxns, 1)) return "Usual basket: minimum purchases must be a whole number of 1 or more";
  if (!isWholeNumber(form.spendWindowDays, 1)) return "Purchases & spend: days of history must be a whole number of 1 or more";
  if (form.defaultMedian.trim() && !isPounds(form.defaultMedian)) return "Default usual basket must be an amount in £, e.g. 25 or 25.50";
  const names = form.rules.map((r) => r.name.trim());
  if (!names.includes(EXISTING)) return `The ${EXISTING} cohort is required`;
  const blank = names.findIndex((n) => !n);
  if (blank >= 0) return `Rule ${blank + 1} needs a cohort name`;
  const duplicate = names.find((n, i) => names.indexOf(n) !== i);
  if (duplicate) return `Two rules are called ${duplicate}; cohort names must be unique`;
  for (const r of form.rules.filter((x) => !x.isRemainder)) {
    if (!r.criteria.length) return `${r.name} needs at least one condition`;
    for (const c of r.criteria) {
      if ((c.type === "group_purchase" || c.type === "group_activity") && !c.groupId) return `${r.name}: pick a competitor group`;
      if (c.type === "group_activity") {
        if (!c.minTxns.trim() && !c.minSpend.trim()) return `${r.name}: fill the minimum purchases, the minimum spend, or both`;
        if (c.minTxns.trim() && !isWholeNumber(c.minTxns, 1)) return `${r.name}: minimum purchases must be a whole number of 1 or more`;
        if (c.minSpend.trim() && (!isPounds(c.minSpend) || Number(c.minSpend) <= 0)) return `${r.name}: minimum spend must be an amount in £ above 0`;
        continue;
      }
      if (!AMOUNT_TYPES.has(c.type) && c.op !== "never" && !isWholeNumber(String(c.days ?? ""), 1)) return `${r.name}: days must be a whole number of 1 or more`;
      if (AMOUNT_TYPES.has(c.type) && !isPounds(c.pounds)) return `${r.name}: enter an amount in £, e.g. 20 or 20.50`;
    }
  }
  return null;
};

const describeCondition = (c: any, groups: any[], spendWindowDays: string): string => {
  if (AMOUNT_TYPES.has(c.type)) {
    const what = c.type === "median_basket" ? "usual basket" : `spend in the last ${spendWindowDays || "?"} days`;
    return `${what} ${AMOUNT_OPS[c.op]} £${c.pounds || "?"}`;
  }
  if (c.type === "group_activity") {
    const name = groups.find((g) => Number(g.id) === Number(c.groupId))?.name || "a competitor group";
    const parts = [c.minTxns.trim() && `${c.minTxns}+ purchases`, c.minSpend.trim() && `£${c.minSpend}+ spend`].filter(Boolean);
    return `made ${parts.join(" and ") || "?"} at ${name} in the last ${spendWindowDays || "?"} days`;
  }
  const when = c.op === "never" ? "never" : `${RECENCY_OPS[c.op]} ${c.days || "?"} days`;
  if (c.type === "group_purchase") {
    const name = groups.find((g) => Number(g.id) === Number(c.groupId))?.name || "a competitor group";
    return `shopped at ${name} ${when}`;
  }
  if (c.type === "channel_purchase") return `shopped at this retailer ${CHANNELS[c.channel]} ${when}`;
  return `shopped at this retailer ${SOURCES[c.source]} ${when}`;
};

const describeRule = (r: RuleForm, groups: any[], spendWindowDays: string): string => {
  const outcome = r.targeted ? "get this cohort's offer" : "get no offer and earn nothing";
  if (r.isRemainder) return `Everyone who matched none of the rules above ${outcome}.`;
  if (!r.criteria.length) return "Add a condition.";
  return `Customers who ${r.criteria.map((c) => describeCondition(c, groups, spendWindowDays)).join(" AND ")} ${outcome}.`;
};

const ConditionRow: React.FC<{
  value: any;
  groups: any[];
  readOnly: boolean;
  onChange: (value: any) => void;
  onRemove: () => void;
}> = ({ value, groups, readOnly, onChange, onRemove }) => {
  const amount = AMOUNT_TYPES.has(value.type);
  const set = (patch: object) => onChange({ ...value, ...patch });
  const setOp = (op: string) =>
    set(!amount && op !== "never" && !value.days ? { op, days: CONDITION_TEMPLATES[value.type].days } : { op });
  return (
    <Box>
      <Grid container spacing={1} alignItems="center">
        <Grid item xs={12} md={4}>
          <TextField select fullWidth size="small" label="Condition" value={value.type} disabled={readOnly}
            onChange={(e) => onChange({ ...CONDITION_TEMPLATES[e.target.value] })}>
            {Object.entries(CONDITION_TYPES).map(([k, label]) => <MenuItem key={k} value={k}>{label}</MenuItem>)}
          </TextField>
        </Grid>
        {value.type === "retailer_purchase" && (
          <Grid item xs={6} md={2}>
            <TextField select fullWidth size="small" label="Paid" value={value.source} disabled={readOnly} onChange={(e) => set({ source: e.target.value })}>
              {Object.entries(SOURCES).map(([k, label]) => <MenuItem key={k} value={k}>{label}</MenuItem>)}
            </TextField>
          </Grid>
        )}
        {(value.type === "group_purchase" || value.type === "group_activity") && (
          <Grid item xs={6} md={2}>
            <TextField select fullWidth size="small" label="Group" value={value.groupId} disabled={readOnly}
              error={!value.groupId} onChange={(e) => set({ groupId: e.target.value })}>
              {groups.map((g) => <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>)}
              {groups.length === 0 && <MenuItem value="" disabled>No groups yet: add one in Competitor groups</MenuItem>}
            </TextField>
          </Grid>
        )}
        {value.type === "channel_purchase" && (
          <Grid item xs={6} md={2}>
            <TextField select fullWidth size="small" label="Channel" value={value.channel} disabled={readOnly} onChange={(e) => set({ channel: e.target.value })}>
              {Object.entries(CHANNELS).map(([k, label]) => <MenuItem key={k} value={k}>{label}</MenuItem>)}
            </TextField>
          </Grid>
        )}
        {value.type === "group_activity" && (
          <>
            <Grid item xs={6} md={2}>
              <TextField fullWidth size="small" label="Min purchases" value={value.minTxns} disabled={readOnly}
                error={!!value.minTxns.trim() && !isWholeNumber(value.minTxns, 1)} onChange={(e) => set({ minTxns: e.target.value })} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField fullWidth size="small" label="Min spend £" value={value.minSpend} disabled={readOnly}
                error={!!value.minSpend.trim() && !isPounds(value.minSpend)} onChange={(e) => set({ minSpend: e.target.value })} />
            </Grid>
          </>
        )}
        {value.type !== "group_activity" && <Grid item xs={6} md={2}>
          <TextField select fullWidth size="small" label="When" value={value.op} disabled={readOnly} onChange={(e) => setOp(e.target.value)}>
            {Object.entries(amount ? AMOUNT_OPS : RECENCY_OPS).map(([k, label]) => <MenuItem key={k} value={k}>{label}</MenuItem>)}
          </TextField>
        </Grid>}
        {amount && (
          <Grid item xs={6} md={2}>
            <TextField fullWidth size="small" label="Amount £" value={value.pounds ?? ""} disabled={readOnly}
              error={!isPounds(value.pounds ?? "")} onChange={(e) => set({ pounds: e.target.value })} />
          </Grid>
        )}
        {!amount && value.type !== "group_activity" && value.op !== "never" && (
          <Grid item xs={6} md={2}>
            <TextField fullWidth size="small" label="Days" value={value.days ?? ""} disabled={readOnly}
              error={!isWholeNumber(String(value.days ?? ""), 1)} onChange={(e) => set({ days: e.target.value })} />
          </Grid>
        )}
        {!readOnly && (
          <Grid item xs={2} md={1}>
            <IconButton size="small" title="Remove condition" color="secondary" onClick={onRemove}><DeleteIcon fontSize="small" /></IconButton>
          </Grid>
        )}
      </Grid>
      <Typography variant="caption" color="textSecondary">{CONDITION_HELP[value.type]}</Typography>
    </Box>
  );
};

const DRY_RUN_LABELS: Record<string, string> = {
  not_targeted: "No offer (matched a rule without offers)",
  no_consent: "Not placed: no live bank consent",
};

const CohortRulesTab: React.FC = () => {
  const notify = useNotify();
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [groups, setGroups] = useState<any[]>([]);
  const [campaignId, setCampaignId] = useState<string>("");
  const [definitions, setDefinitions] = useState<any[]>([]);
  const [form, setForm] = useState<DefinitionForm | null>(null);
  const [savedForm, setSavedForm] = useState<string | null>(null);
  const [dryRun, setDryRun] = useState<any | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmingPublish, setConfirmingPublish] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [c, g] = await Promise.all([
          cloGet(ENDPOINTS.CLO_CAMPAIGNS, { page: 1, pageSize: 100 }),
          cloGet(ENDPOINTS.CLO_MERCHANT_GROUPS, { page: 1, pageSize: 100 }),
        ]);
        setCampaigns(c.data || []);
        setGroups(g.data || []);
      } catch (e) {
        notify(cloError(e), "warning");
      }
    })();
  }, [notify]);

  const load = useCallback(async () => {
    if (!campaignId) {
      setDefinitions([]);
      return;
    }
    try {
      const res = await cloGet(ENDPOINTS.CLO_COHORT_DEFINITIONS, { campaignId });
      setDefinitions(res.data || []);
    } catch (e) {
      notify(cloError(e), "warning");
    }
  }, [campaignId, notify]);

  useEffect(() => {
    load();
  }, [load]);

  const open = async (id: number) => {
    try {
      const res = await cloGet(`${ENDPOINTS.CLO_COHORT_DEFINITIONS}/${id}`);
      const loaded = fromDefinition(res.data);
      setForm(loaded);
      setSavedForm(snapshot(loaded));
    } catch (e) {
      notify(cloError(e), "warning");
    }
  };

  const openNew = async () => {
    const latest = definitions[0];
    if (!latest) {
      try {
        const res = await cloGet(`${ENDPOINTS.CLO_CAMPAIGNS}/${campaignId}`);
        const names: string[] = (res.data?.versions?.[0]?.cohorts || []).map((c: any) => c.cohort_name);
        setForm({ atvWindowDays: "365", spendWindowDays: "365", atvMinTxns: "3", defaultMedian: "", rules: rulesFromPricing(names) });
      } catch (e) {
        notify(cloError(e), "warning");
      }
      return;
    }
    try {
      const res = await cloGet(`${ENDPOINTS.CLO_COHORT_DEFINITIONS}/${latest.id}`);
      const copy = fromDefinition(res.data);
      const rules = copy.rules.some((r) => r.name === EXISTING) ? copy.rules : [DEFAULT_RULES()[0], ...copy.rules];
      setForm({ ...copy, rules, id: undefined, version: undefined, status: undefined, dryRunAt: null });
    } catch (e) {
      notify(cloError(e), "warning");
    }
  };

  const persist = async (): Promise<DefinitionForm | null> => {
    if (!form) return null;
    const error = formError(form);
    if (error) {
      notify(error, "warning");
      return null;
    }
    try {
      const body = toBody(form);
      const res = form.id
        ? await cloPatch(`${ENDPOINTS.CLO_COHORT_DEFINITIONS}/${form.id}`, body)
        : await cloPost(ENDPOINTS.CLO_COHORT_DEFINITIONS, { ...body, campaignId: Number(campaignId) });
      const saved = fromDefinition(res.data);
      setForm(saved);
      setSavedForm(snapshot(saved));
      load();
      return saved;
    } catch (e) {
      notify(cloError(e), "warning");
      return null;
    }
  };

  const runDryRun = async (definitionId: number) => {
    setBusy(true);
    try {
      const res = await cloPost(ENDPOINTS.CLO_COHORT_ACTIONS, { action: "dry_run", definitionId });
      setDryRun(res.data);
      setForm((f) => (f && f.id === definitionId ? { ...f, dryRunAt: res.data.ranAt } : f));
    } catch (e) {
      notify(cloError(e), "warning");
    } finally {
      setBusy(false);
    }
  };

  const saveDraft = async () => {
    if (await persist()) notify("Draft saved", "info");
  };

  const dryRunFromEditor = async () => {
    if (!form) return;
    if (readOnly) return runDryRun(form.id as number);
    const saved = await persist();
    if (saved?.id) runDryRun(saved.id);
  };

  const publish = async () => {
    if (!form?.id || !canPublish) return;
    setConfirmingPublish(false);
    try {
      await cloPost(ENDPOINTS.CLO_COHORT_ACTIONS, { action: "publish", definitionId: form.id });
      notify(`Cohort rules v${form.version} published`, "info");
      setForm(null);
      load();
    } catch (e) {
      notify(cloError(e), "warning");
    }
  };

  const readOnly = form?.status === "published";
  const unsaved = form !== null && (!form.id || snapshot(form) !== savedForm);
  const canPublish = Boolean(form?.id) && !unsaved && Boolean(form?.dryRunAt) && !busy;
  const setRule = (uid: string, patch: Partial<RuleForm>) =>
    form && setForm({ ...form, rules: form.rules.map((r) => (r.uid === uid ? { ...r, ...patch } : r)) });
  const move = (index: number, by: number) => {
    if (!form) return;
    const rules = [...form.rules];
    [rules[index], rules[index + by]] = [rules[index + by], rules[index]];
    setForm({ ...form, rules });
  };
  const addRule = () => {
    if (!form) return;
    const rules = [...form.rules];
    rules.splice(rules.length - 1, 0, rule("", [{ ...CONDITION_TEMPLATES.retailer_purchase }]));
    setForm({ ...form, rules });
  };
  const hasDraft = definitions.some((d) => d.status === "draft");

  return (
    <Paper>
      <Box p={2} display="flex" alignItems="center" style={{ gap: 16 }}>
        <TextField select label="Campaign" value={campaignId} onChange={(e) => setCampaignId(e.target.value)} style={{ minWidth: 320 }}>
          {campaigns.map((c) => <MenuItem key={c.id} value={String(c.id)}>{c.retailer_name} · {c.name}</MenuItem>)}
        </TextField>
        <Box flexGrow={1} />
        <Button color="primary" variant="contained" disabled={!campaignId || hasDraft} onClick={openNew}>New version</Button>
      </Box>
      <Box px={2}>
        <Typography variant="body2" color="textSecondary" component="div">
          Cohort rules decide which cohort each customer is in for a campaign (e.g. Existing, Lapsed, Competitor), and so which offer they get.
          <ul style={{ margin: "4px 0", paddingLeft: 20 }}>
            <li>Rules are checked top to bottom. A customer goes into the first rule they match; the last rule catches everyone else.</li>
            <li>Every rule that gets offers needs pricing with the same cohort name in the campaign version (Campaign & Merchant Config).</li>
            <li>Draft = editable. Published = frozen; to change it, create a new version (it starts as a copy of the latest).</li>
            <li>Data used: open banking, Sprive gift cards (SWS) and VDX purchases when a customer is first placed; re-checks also use their linked-card (Visa) purchases.</li>
            <li>A purchase that earns a one-time reward moves the customer to <b>{EXISTING}</b>.</li>
            <li>The nightly re-check only moves customers out of <b>{EXISTING}</b>, once their last shop falls outside its window.</li>
            <li>Other cohorts change only when a new rules version goes live: then every customer in the campaign is re-checked against it.</li>
            <li><b>{EXISTING}</b> is always part of the rules: you can edit its conditions and move it, but not rename or remove it, and it always gets offers.</li>
          </ul>
          {hasDraft && "This campaign already has a draft; open it to edit or publish it before starting another version."}
        </Typography>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Version</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Rules</TableCell>
            <TableCell>Published</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {definitions.map((d) => (
            <TableRow key={d.id} hover>
              <TableCell>v{d.version}</TableCell>
              <TableCell><Chip size="small" label={d.status} color={d.status === "published" ? "primary" : "default"} /></TableCell>
              <TableCell>{d.rule_count}</TableCell>
              <TableCell>{d.published_at ? `${formatDate(d.published_at)} · ${d.published_by}` : "—"}</TableCell>
              <TableCell align="right">
                <Button size="small" disabled={busy} onClick={() => runDryRun(d.id)}>Dry run</Button>
                <Button size="small" color="primary" onClick={() => open(d.id)}>{d.status === "draft" ? "Edit" : "View"}</Button>
              </TableCell>
            </TableRow>
          ))}
          {definitions.length === 0 && (
            <TableRow><TableCell colSpan={5} align="center">{campaignId ? "No cohort rules yet. Press New version to start from the usual four cohorts." : "Pick a campaign"}</TableCell></TableRow>
          )}
        </TableBody>
      </Table>

      <Dialog disableBackdropClick open={form !== null} onClose={() => setForm(null)} maxWidth="lg" fullWidth>
        <DialogTitle>
          {form?.version ? `Cohort rules v${form.version} · ${form.status === "published" ? "published (read only)" : "draft"}` : "New cohort rules (not saved yet)"}
        </DialogTitle>
        {form && (
          <DialogContent>
            <Typography variant="subtitle2" gutterBottom>Usual basket (median spend per visit)</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="Days of history" value={form.atvWindowDays} disabled={readOnly}
                  error={!isWholeNumber(form.atvWindowDays, 1)}
                  helperText="Purchases older than this are ignored. Takes effect once customer purchase stats are live."
                  onChange={(e) => setForm({ ...form, atvWindowDays: e.target.value })} />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="Minimum purchases" value={form.atvMinTxns} disabled={readOnly}
                  error={!isWholeNumber(form.atvMinTxns, 1)}
                  helperText="Fewer purchases than this and the customer's own median is not trusted."
                  onChange={(e) => setForm({ ...form, atvMinTxns: e.target.value })} />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="Default usual basket £" value={form.defaultMedian} disabled={readOnly}
                  error={!!form.defaultMedian.trim() && !isPounds(form.defaultMedian)}
                  helperText="Used for 'above usual basket' rewards (rule D) when a customer has too few purchases. Empty = none."
                  onChange={(e) => setForm({ ...form, defaultMedian: e.target.value })} />
              </Grid>
            </Grid>

            <Typography variant="subtitle2" style={{ marginTop: 16 }} gutterBottom>Purchases & spend</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="Days of history" value={form.spendWindowDays} disabled={readOnly}
                  error={!isWholeNumber(form.spendWindowDays, 1)}
                  helperText="Window for 'Spend at this retailer' and 'Purchases / spend at a competitor group'."
                  onChange={(e) => setForm({ ...form, spendWindowDays: e.target.value })} />
              </Grid>
            </Grid>

            <Typography variant="subtitle2" style={{ marginTop: 16 }}>Rules (checked top to bottom)</Typography>
            {form.rules.map((r, i) => (
              <Paper key={r.uid} variant="outlined" style={{ padding: 12, marginTop: 12 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={1}><Typography variant="h6">{i + 1}</Typography></Grid>
                  <Grid item xs={11} md={3}>
                    <TextField fullWidth label="Cohort name" value={r.name} disabled={readOnly || r.name === EXISTING} error={!r.name.trim()}
                      helperText={r.name === EXISTING ? "Built-in: customers fall back here" : "Must match a cohort in the campaign's pricing"}
                      onChange={(e) => setRule(r.uid, { name: e.target.value })} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControlLabel label="Gets offers" control={
                      <Checkbox checked={r.name === EXISTING || r.targeted} disabled={readOnly || r.name === EXISTING}
                        onChange={(e) => setRule(r.uid, { targeted: e.target.checked })} />
                    } />
                    <Typography variant="caption" color="textSecondary" display="block">
                      {r.name === EXISTING
                        ? "Always gets offers and is never one-time."
                        : "Gets offers: untick to hold these customers out (no offer, nothing earned or billed)."}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4} style={{ textAlign: "right" }}>
                    {r.isRemainder ? <Chip size="small" label="Everyone else · always last" /> : !readOnly && (
                      <>
                        <IconButton size="small" title="Move up" disabled={i === 0} onClick={() => move(i, -1)}><ArrowUpwardIcon fontSize="small" /></IconButton>
                        <IconButton size="small" title="Move down" disabled={i >= form.rules.length - 2} onClick={() => move(i, 1)}><ArrowDownwardIcon fontSize="small" /></IconButton>
                        <IconButton size="small" title={r.name === EXISTING ? `${EXISTING} can't be removed` : "Remove rule"} color="secondary"
                          disabled={r.name === EXISTING} onClick={() => setForm({ ...form, rules: form.rules.filter((x) => x.uid !== r.uid) })}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </>
                    )}
                  </Grid>
                  {!r.isRemainder && (
                    <Grid item xs={12}>
                      {r.criteria.map((c, ci) => (
                        <Box key={ci} mt={1}>
                          <ConditionRow
                            value={c} groups={groups} readOnly={readOnly}
                            onChange={(value) => setRule(r.uid, { criteria: r.criteria.map((x, xi) => (xi === ci ? value : x)) })}
                            onRemove={() => setRule(r.uid, { criteria: r.criteria.filter((_, xi) => xi !== ci) })}
                          />
                        </Box>
                      ))}
                      {!readOnly && (
                        <Button size="small" color="primary" style={{ marginTop: 8 }}
                          onClick={() => setRule(r.uid, { criteria: [...r.criteria, { ...CONDITION_TEMPLATES.retailer_purchase }] })}>
                          + And condition (all must be true)
                        </Button>
                      )}
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Typography variant="body2" style={{ fontStyle: "italic" }}>{describeRule(r, groups, form.spendWindowDays)}</Typography>
                  </Grid>
                </Grid>
              </Paper>
            ))}
            {!readOnly && <Button style={{ marginTop: 12 }} variant="outlined" color="primary" onClick={addRule}>+ Add rule (above Everyone else)</Button>}
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setForm(null)}>Close</Button>
          <Button disabled={busy} onClick={dryRunFromEditor}>{readOnly ? "Dry run" : "Save & dry run"}</Button>
          {!readOnly && <Button color="primary" onClick={saveDraft}>Save draft</Button>}
          {!readOnly && !canPublish && (
            <span style={{ fontSize: 12, color: "#888", marginRight: 8 }}>
              {unsaved ? "Save & dry run to enable publish" : "Run a dry run to enable publish"}
            </span>
          )}
          {!readOnly && <Button color="primary" variant="contained" disabled={!canPublish} onClick={() => setConfirmingPublish(true)}>Publish</Button>}
        </DialogActions>
      </Dialog>

      <Dialog open={dryRun !== null} onClose={() => setDryRun(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Dry run · {dryRun?.total ?? 0} customers checked</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            {dryRun?.moves ?? 0} current member(s) would change cohort if these rules applied today.
          </Typography>
          {dryRun?.offerRanInWindow && (
            <Typography variant="body2" style={{ color: "#b26a00" }} gutterBottom>
              ⚠ An offer for this retailer ran in the last 28 days, so the organic rate can't be measured now.
            </Typography>
          )}
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Cohort</TableCell>
                <TableCell align="right">Customers</TableCell>
                <TableCell align="right">Organic rate</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(dryRun?.cohorts || {}).map(([name, count]) => (
                <TableRow key={name}>
                  <TableCell>{DRY_RUN_LABELS[name] || name}</TableCell>
                  <TableCell align="right">{String(count)}</TableCell>
                  <TableCell align="right">{dryRun?.organicRatePct ? `${dryRun.organicRatePct[name] ?? 0}%` : "n/a"}</TableCell>
                </TableRow>
              ))}
              {Object.keys(dryRun?.cohorts || {}).length === 0 && (
                <TableRow><TableCell colSpan={3} align="center">No customers to check yet</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
          <Typography variant="caption" color="textSecondary" component="div" style={{ marginTop: 8 }}>
            No customer is moved. The counts are kept so the campaign Approve step can check control group sizes.
            Customers checked: everyone already in this campaign, plus opted-in customers once opt-in is live.
            <br />
            Organic rate: share of the cohort who shopped at the retailer in the last 28 days without an offer (bank payment, Sprive gift card or VDX).
            It is the baseline Control & measurement must beat.
            <br />
            Until customer purchase stats are live, most customers fall into the last rule and organic rates show 0%.
          </Typography>
        </DialogContent>
        <DialogActions><Button onClick={() => setDryRun(null)}>Close</Button></DialogActions>
      </Dialog>
      <Confirm
        isOpen={confirmingPublish}
        title={`Publish cohort rules v${form?.version}?`}
        content="Once published they can't be edited; changes need a new version."
        confirm="Publish"
        onConfirm={publish}
        onClose={() => setConfirmingPublish(false)}
      />
    </Paper>
  );
};

export default CohortRulesTab;
